import type { AdminAppealDetailDto, AdminAppealEvidenceRefDto } from '@carcheck/shared';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { EvidenceLightbox } from './EvidenceLightbox';
import { EvidenceTile } from './EvidenceTile';
import { publishToast } from '../../../components/feedback';
import { tokens } from '../../../design';

/**
 * Evidence gallery for the appeal detail drawer (AUT-132 — replaces the
 * AUT-130 `EvidencePlaceholder`). Renders `detail.evidence[]` (short-lived
 * signed GET URLs) as the mockup `.evidence-strip`: a horizontal row of
 * 88×64 tiles + a "5 минут" TTL hint.
 *
 *   - `kind: 'photo'`    → thumbnail tile; click opens the full-screen
 *     `EvidenceLightbox` at that photo's index (photos-only ordering).
 *   - `kind: 'document'` → a document tile that opens the signed `url` in
 *     a new tab.
 *
 * `evidenceCount` vs `evidence.length`: the fail-closed backend minter
 * omits foreign / legacy-unscoped keys, so `evidenceCount` MAY EXCEED
 * `evidence.length`. When it does, a subtle "{n} вложений недоступны" note
 * tells the admin some keys were skipped. `evidenceCount === 0` shows an
 * empty state.
 *
 * TTL: the URLs are ~5-min signed and were minted by the detail query.
 * No auto-refresh — an `<img>` 403 after expiry surfaces a toast hinting
 * to re-open the drawer (the re-fetch re-mints). Simplest correct UX.
 */
export interface EvidenceGalleryProps {
  detail: AdminAppealDetailDto;
}

const SECTION_TITLE_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.6px',
  color: tokens.text.tertiary,
  mb: 1.5,
};

const HINT_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.625,
  mt: 1,
  fontSize: 11,
  color: tokens.text.tertiary,
};

/**
 * The horizontal tile strip. Computes per-tile labels in one pure pass
 * (type-local running index) and routes a click: a photo opens the
 * lightbox at its photos-only index; a document opens its signed URL in
 * a new tab (`noopener`).
 */
const EvidenceStrip = ({
  evidence,
  onPhotoOpen,
  onPhotoError,
}: {
  evidence: AdminAppealEvidenceRefDto[];
  onPhotoOpen: (photoIndex: number) => void;
  onPhotoError: () => void;
}): ReactElement => {
  const { t } = useTranslation();

  let photoIdx = 0;
  let docIdx = 0;
  const tiles = evidence.map((item) => {
    if (item.kind === 'photo') {
      const photoPos = photoIdx;
      photoIdx += 1;
      return {
        item,
        onClick: () => onPhotoOpen(photoPos),
        onLoadError: onPhotoError,
        label: t('appeals.evidence.photoLabel', { index: photoIdx }),
        tag: t('appeals.evidence.photoTag'),
      };
    }
    docIdx += 1;
    const { url } = item;
    return {
      item,
      onClick: () => window.open(url, '_blank', 'noopener,noreferrer'),
      onLoadError: undefined,
      label: t('appeals.evidence.documentOpen', { index: docIdx }),
      tag: t('appeals.evidence.documentTag'),
    };
  });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
      {tiles.map(({ item, onClick, onLoadError, label, tag }, index) => (
        <EvidenceTile
          key={`${item.kind}-${index}`}
          evidence={item}
          label={label}
          tag={tag}
          onClick={onClick}
          {...(onLoadError ? { onLoadError } : {})}
        />
      ))}
    </Box>
  );
};

/** Empty state when no evidence keys are attached at all. */
const EvidenceEmpty = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography sx={SECTION_TITLE_SX}>{t('appeals.evidence.title')}</Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <ImageOutlinedIcon fontSize="small" sx={{ color: tokens.text.tertiary }} />
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
          {t('appeals.evidence.empty')}
        </Typography>
      </Box>
    </Box>
  );
};

export const EvidenceGallery = ({ detail }: EvidenceGalleryProps): ReactElement => {
  const { t } = useTranslation();
  const { evidence, evidenceCount } = detail;
  const [openPhotoIndex, setOpenPhotoIndex] = useState<number | null>(null);

  // Photos-only ordering for the lightbox; documents are excluded.
  const photoUrls = useMemo(
    () => evidence.filter((e) => e.kind === 'photo').map((e) => e.url),
    [evidence],
  );

  const onPhotoError = useCallback((): void => {
    publishToast({
      severity: 'warning',
      durationMs: 4000,
      message: t('appeals.evidence.urlExpired'),
    });
  }, [t]);

  if (evidenceCount === 0) return <EvidenceEmpty />;

  // Skipped (unscoped / legacy) keys the fail-closed minter omitted.
  const unavailableCount = Math.max(0, evidenceCount - evidence.length);

  return (
    <Box>
      <Typography sx={SECTION_TITLE_SX}>{t('appeals.evidence.title')}</Typography>

      <EvidenceStrip
        evidence={evidence}
        onPhotoOpen={setOpenPhotoIndex}
        onPhotoError={onPhotoError}
      />

      {unavailableCount > 0 ? (
        <Box sx={{ ...HINT_SX, color: tokens.state.warning }}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />
          <Typography variant="caption" sx={{ color: tokens.state.warning }}>
            {t('appeals.evidence.unavailableNote', { count: unavailableCount })}
          </Typography>
        </Box>
      ) : null}

      <Box sx={HINT_SX}>
        <ScheduleOutlinedIcon sx={{ fontSize: 13 }} />
        <Typography variant="caption" sx={{ color: tokens.text.tertiary }}>
          {t('appeals.evidence.ttlHint')}
        </Typography>
      </Box>

      <EvidenceLightbox
        open={openPhotoIndex !== null}
        photos={photoUrls}
        currentIndex={openPhotoIndex ?? 0}
        onClose={() => setOpenPhotoIndex(null)}
        onIndexChange={(next) => setOpenPhotoIndex(next)}
        onPhotoError={onPhotoError}
      />
    </Box>
  );
};
